import { formatDateShort, formatDateTimeShort } from "@entrilia/shared/utilities/dateUtils.ts";
import {
  formatMoney,
  formatNumber as formatNumberIntl,
  formatPercentage,
} from "@entrilia/shared/utilities/formatters.ts";
import { stringifyValue } from "@entrilia/shared/utilities/stringHelper.ts";
import { isDate } from "date-fns";
import { isNumeral } from "numeral";
import type { NumberFormatterOptions } from "../../../../../api/types/fieldConfigurationTypes.ts";
import { FormatType, NegativeValueAs } from "../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  DimensionDescriptor,
  FormatterDescription,
  MetaFormat,
  ReportField,
} from "../../../../../api/types/svTypes.ts";
import { getCurrentLocale } from "../../utils/dateUtils.ts";

export const formatDimension = (field: DimensionDescriptor, value: unknown) => {
  if (typeof value === "object" || value === undefined) {
    return "";
  }
  switch (field.defaultFormat?.formatType) {
    case FormatType.Numeric: {
      const parsedValue = isNumeral(value) ? (value as number) : parseFloat(value as string);
      return formatNumber(parsedValue, field.defaultFormat);
    }
    case FormatType.Date: {
      const parsedValue = isDate(value) ? value : new Date(value as string);
      return formatDateShort(parsedValue);
    }
    case FormatType.DateTime: {
      const parsedValue = isDate(value) ? value : new Date(value as string);
      return formatDateTimeShort(parsedValue);
    }
    case FormatType.BlankIfZero: {
      return value === "0" || value === 0 ? "" : stringifyValue(value);
    }
    case FormatType.BlankIfFalse: {
      return value === false || value === "false" || value === "False" ? "" : stringifyValue(value);
    }
    default: {
      return stringifyValue(value);
    }
  }
};

export const formatMeasure = (field: ReportField | undefined, value: number) => {
  if (field === undefined) return `${value}`;
  switch (field.config.format?.formatType) {
    case FormatType.Numeric: {
      return formatNumber(value, field.config.format);
    }
    default:
      return `${value}`;
  }
};

const getFactorValue = (abbreviation: string | undefined): number => {
  switch (abbreviation) {
    case "K":
      return 1_000;
    case "M":
      return 1_000_000;
    case "B":
      return 1_000_000_000;
    default:
      return 1;
  }
};

export const formatNumber = (value: number, incomeOptions: NumberFormatterOptions | undefined, localeCode?: string) => {
  if (isNaN(value)) {
    return "";
  }
  if (!incomeOptions) {
    return value + "";
  }

  const options = { ...incomeOptions } as NumberFormatterOptions;
  let formattedValue: string;

  // Build Intl.NumberFormat options
  const formatOptions: Intl.NumberFormatOptions = {};

  // Handle decimals
  const decimals = options.decimals ?? 0;
  if (decimals > 0) {
    if (options.autoDecimals) {
      formatOptions.minimumFractionDigits = 0;
      formatOptions.maximumFractionDigits = decimals;
    } else {
      formatOptions.minimumFractionDigits = decimals;
      formatOptions.maximumFractionDigits = decimals;
    }
  } else {
    formatOptions.minimumFractionDigits = 0;
    formatOptions.maximumFractionDigits = 0;
  }

  // Handle abbreviation (divide by factor, but don't add suffix)
  const factorValue = getFactorValue(options.abbreviation);
  const valueToFormat = options.abbreviation ? value / factorValue : value;

  // Handle convertToPercentage (multiply by 100 and format as percentage)
  if (options.convertToPercentage) {
    formattedValue = formatPercentage(valueToFormat, formatOptions, localeCode);
  }
  // Handle percentage without conversion (just add % sign)
  else if (options.percentage) {
    formattedValue = formatNumberIntl(valueToFormat, formatOptions, localeCode);
    formattedValue += "%";
  }
  // Handle currency
  else if (options.formatType === FormatType.Currency) {
    if (options.showNegativeValuesAs === NegativeValueAs.Parentheses) {
      formatOptions.currencySign = "accounting";
    }
    // formatMoney requires ISO currency code, but we might have just a symbol like "$"
    // Try to use formatMoney if it looks like an ISO code (3 letters), otherwise use formatNumber + prefix
    const currencyCode = options.currency.trim();
    if (currencyCode.length === 3 && /^[A-Z]{3}$/i.test(currencyCode)) {
      formattedValue = formatMoney(valueToFormat, currencyCode, formatOptions, localeCode);
    } else {
      // Use formatNumber and add currency as prefix
      formattedValue = formatNumberIntl(valueToFormat, formatOptions, localeCode);
      formattedValue = currencyCode + formattedValue;
    }
  }
  // Default: format as regular number
  else {
    formattedValue = formatNumberIntl(valueToFormat, formatOptions, localeCode);
    if (options.showNegativeValuesAs === NegativeValueAs.Parentheses) {
      formattedValue = value < 0 ? `(${formattedValue.replace("-", "")})` : formattedValue;
    }
  }

  // Add prefix and suffix
  if (options.prefix) {
    formattedValue = options.prefix + formattedValue;
  }
  if (options.suffix) {
    formattedValue += options.suffix;
  }

  return formattedValue;
};

export const getAllNumberFormattersDescription = () => {
  const localeCode = getCurrentLocale().code;
  const result: FormatterDescription[] = [
    {
      title: "Number Rounded",
      subTitle: formatNumber(
        1234,
        {
          formatType: FormatType.Numeric,
          type: "NumberRounded",
          decimals: 0,
        } as NumberFormatterOptions,
        localeCode
      ),
      options: {
        formatType: FormatType.Numeric,
        type: "NumberRounded",
        decimals: 0,
      },
    },
    {
      title: "Number (2 decimals)",
      subTitle: formatNumber(
        1234.12,
        {
          formatType: FormatType.Numeric,
          type: "Number2Decimals",
          decimals: 2,
        } as NumberFormatterOptions,
        localeCode
      ),
      options: {
        formatType: FormatType.Numeric,
        type: "Number2Decimals",
        decimals: 2,
      },
    },
    {
      title: "Group (Dot)",
      subTitle: formatNumber(
        1234000.12,
        {
          formatType: FormatType.Numeric,
          type: "GroupDot",
          decimals: 2,
        } as NumberFormatterOptions,
        localeCode
      ),
      options: {
        formatType: FormatType.Numeric,
        type: "GroupDot",
        decimals: 2,
      },
    },
    {
      title: "Abbreviation (Million)",
      subTitle:
        formatNumber(
          1234000,
          {
            formatType: FormatType.Numeric,
            decimals: 0,
          } as NumberFormatterOptions,
          localeCode
        ) +
        "→" +
        formatNumber(
          1200,
          {
            formatType: FormatType.Numeric,
            abbreviation: "K",
            decimals: 1,
          } as NumberFormatterOptions,
          localeCode
        ),
      options: {
        formatType: FormatType.Numeric,
        type: "AbbreviationM",
        abbreviation: "M",
        decimals: 2,
      },
    },
    {
      title: "Abbreviation (Thousand)",
      subTitle:
        formatNumber(
          1200,
          {
            formatType: FormatType.Numeric,
            decimals: 0,
          } as NumberFormatterOptions,
          localeCode
        ) +
        "→" +
        formatNumber(
          1200,
          {
            formatType: FormatType.Numeric,
            abbreviation: "K",
            decimals: 1,
          } as NumberFormatterOptions,
          localeCode
        ),
      options: {
        formatType: FormatType.Numeric,
        type: "AbbreviationK",
        abbreviation: "K",
        decimals: 2,
      },
    },
    {
      title: "Percent (Rounded)",
      subTitle: "12→12%",
      options: {
        formatType: FormatType.Numeric,
        type: "PercentRounded",
        percentage: true,
      },
    },
    {
      title: "Percent (Converted)",
      subTitle:
        formatNumber(
          0.12,
          {
            formatType: FormatType.Numeric,
            decimals: 2,
          } as NumberFormatterOptions,
          localeCode
        ) + "→12%",
      options: {
        formatType: FormatType.Numeric,
        type: "PercentConverted",
        percentage: true,
        convertToPercentage: true,
      },
    },
    {
      title: "Multiple",
      subTitle:
        formatNumber(
          1.02,
          {
            formatType: FormatType.Numeric,
            decimals: 2,
          } as NumberFormatterOptions,
          localeCode
        ) +
        "→" +
        formatNumber(
          1.02,
          {
            formatType: FormatType.Numeric,
            decimals: 2,
            suffix: "x",
          } as NumberFormatterOptions,
          localeCode
        ),
      options: {
        formatType: FormatType.Numeric,
        type: "Multiple",
        decimals: 2,
        suffix: "x",
      },
    },
  ];
  return result;
};

export const isNumberApplicable = (value: number | undefined, format: MetaFormat | undefined) => {
  if (value === undefined || value === 0) return false;
  return formatNumber(value, format?.formatType === FormatType.Numeric ? format : undefined) !== "";
};

export const defaultTextFormat = {
  formatType: FormatType.None,
} as NumberFormatterOptions;

export const defaultCountAggregationFormat = {
  formatType: FormatType.Numeric,
} as NumberFormatterOptions;

export const defaultNumericFormat = {
  formatType: FormatType.Numeric,
  decimals: 2,
} as NumberFormatterOptions;

export const defaultDateFormat = {
  formatType: FormatType.Date,
} as NumberFormatterOptions;
